// TODO: criar um hook que nem useProofRequestsUpload (tenho no notion) na mtech que lida com upload em todo lugar.
import { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import api from '../services/api';
import history from '../services/history';
import { uploadBlobToLaudo } from '../services/laudoFiles';
import { getFolderName } from '../util/agendaHelpers';

const UPLOAD_CONCURRENCY = 2;
const AVISO_SAIDA =
  'O envio dos arquivos ainda não terminou. Sair agora pode interromper o anexo.';

const motivoFalha = reason =>
  reason?.response?.data?.error ||
  reason?.message ||
  'Erro ao enviar o arquivo.';

const descreverFalhas = (arquivos, results) =>
  arquivos
    .map((file, index) => ({ file, result: results[index] }))
    .filter(({ result }) => result?.status === 'rejected')
    .map(({ file, result }) => `${file.name} (${motivoFalha(result.reason)})`)
    .join(', ');

const settleWithConcurrency = async (items, limit, fn) => {
  const results = new Array(items.length);
  let cursor = 0;

  const runNext = async () => {
    if (cursor >= items.length) return;

    const index = cursor;
    cursor += 1;

    try {
      const value = await fn(items[index]);
      results[index] = { status: 'fulfilled', value };
    } catch (reason) {
      results[index] = { status: 'rejected', reason };
    }

    await runNext();
  };

  const workers = Array.from({ length: Math.min(limit, items.length) }, () =>
    runNext()
  );

  await Promise.all(workers);
  return results;
};

const useUploadFiles = ({ atividade }) => {
  const [files, setFiles] = useState();
  const [isUploading, setIsUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (!isUploading) return undefined;

    const handleBeforeUnload = event => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    const unblock = history.block(AVISO_SAIDA);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      unblock();
    };
  }, [isUploading]);

  const handleUploadFile = useCallback(
    async file => {
      if (!atividade) throw new Error('Atividade não encontrada');

      const folder = getFolderName(atividade);
      const codLaudo =
        atividade.CODLAUDO ||
        atividade.CODECO ||
        atividade.CODTRANS ||
        atividade.CODSTRESS ||
        atividade.CODEXAME || // ERGO = CODEXAME
        atividade.CODHOLTER ||
        atividade.CODMAPA ||
        atividade.CODSCAN ||
        atividade.CODFETAL;

      if (!codLaudo) throw new Error('Laudo não encontrado');

      await uploadBlobToLaudo({
        procedimento: folder,
        codLaudo,
        fileName: file.name,
        body: file,
      });
    },
    [atividade]
  );

  const confirmUpload = useCallback(async () => {
    if (!files || !files.length) return toast.warn('Selecione um arquivo!');

    if (atividade.CODPROCEDI === 3) {
      const { data } = await api.get('/files', {
        params: { codLaudo: atividade.CODLAUDO, procedimento: 'ecg' },
      });
      if (data.length > 0) return toast.warn('ECG já tem um arquivo.');
    }

    try {
      setIsUploading(true);
      const results = await settleWithConcurrency(
        files,
        UPLOAD_CONCURRENCY,
        handleUploadFile
      );

      const failures = results.filter(result => result.status === 'rejected');
      const successes = results.length - failures.length;
      const failedFiles = files.filter(
        (_, index) => results[index].status === 'rejected'
      );
      const sentryExtra = {
        atividade: {
          codLaudo: atividade?.CODLAUDO,
          codProcedi: atividade?.CODPROCEDI,
        },
        files: files.map(f => ({ name: f.name, size: f.size })),
        failedFiles: failedFiles.map(f => ({ name: f.name, size: f.size })),
      };

      const falhasDescricao = descreverFalhas(files, results);
      const motivos = failures.map(f => motivoFalha(f.reason));

      if (failures.length === results.length) {
        Sentry.captureException(failures[0].reason, {
          level: 'error',
          extra: {
            ...sentryExtra,
            failures: motivos,
          },
        });
        toast.error(`Não foi possível enviar ${falhasDescricao}.`);
        return;
      }

      if (failures.length > 0) {
        Sentry.captureException(failures[0].reason, {
          level: 'error',
          extra: {
            ...sentryExtra,
            failures: motivos,
          },
        });
        toast.warn(`${successes} enviado(s). Não enviado: ${falhasDescricao}.`);
        setFiles(
          files.filter((_, index) => results[index].status === 'rejected')
        );
        return;
      }

      toast.success('Arquivos enviados com sucesso!');
      setFiles([]);
      setShowUpload(false);
    } finally {
      setIsUploading(false);
    }
  }, [files, handleUploadFile, atividade]);

  const handleCloseModal = useCallback(() => {
    setShowUpload(false);
    setFiles([]);
  }, []);

  return {
    files,
    setFiles,
    isUploading,
    showUpload,
    setShowUpload,
    confirmUpload,
    handleCloseModal,
  };
};

export default useUploadFiles;
